import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { ModalOpenButton, useScreenWidth } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import moment from "moment";
import React from "react";
import { PdsSyncEntityType } from "../../../clients/graphqlTypes.js";
import { PdsIcon } from "./PdsIcon.js";
import { PdsSyncReviewModal } from "./PdsSyncReviewModal.js";
import { PdsSyncReviewAccess } from "./usePdsSyncReviewAccess.js";

interface Props {
  access: PdsSyncReviewAccess;
  defaultEntityType: PdsSyncEntityType;
}

/**
 * Permanent PDS Sync entry point + feature advertisement — a white outlined
 * surface that is always visible while the integration is enabled (regardless
 * of pending/failed counts). The "View log" button opens the log; an errors
 * chip appears only when there are execution failures and deep-links to the
 * failed log. On phones the title/chips stack vertically while the button
 * stays to the side.
 */
export function PdsSyncBanner({ access, defaultEntityType }: Props) {
  const { t } = useTranslate(["Integrations", "Global"]);
  const { isMinTablet } = useScreenWidth();

  if (!access.isEnabled) return null;

  const failed = access.counts?.failed ?? 0;

  const lastSyncLabel = access.lastSyncAt
    ? moment(access.lastSyncAt).fromNow(true)
    : null;

  const viewLogButton = (
    <ModalOpenButton
      Modal={PdsSyncReviewModal}
      modalProps={{
        access,
        defaultEntityType,
      }}
    >
      <Button size="small" variant="outlined" color="primary">
        {t("View log", { ns: "Integrations" })}
      </Button>
    </ModalOpenButton>
  );

  return (
    <Paper variant="outlined" className="sentry-mask" sx={{ p: 1.5, mb: 2 }}>
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        sx={{ alignItems: "center" }}
      >
        <PdsIcon className="sentry-mask" />
        <Stack
          direction={isMinTablet ? "row" : "column"}
          spacing={1}
          useFlexGap
          sx={{
            flex: 1,
            minWidth: 0,
            alignItems: isMinTablet ? "center" : "flex-start",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="subtitle2">PDS Sync</Typography>
          {lastSyncLabel && (
            <Chip
              size="small"
              variant="outlined"
              label={t("Last sync {time} ago", {
                ns: "Integrations",
                time: lastSyncLabel,
              })}
            />
          )}
          {failed > 0 && (
            <ModalOpenButton
              Modal={PdsSyncReviewModal}
              modalProps={{
                access,
                defaultEntityType,
                initialTab: "history" as const,
                initialStatusFilter: ["FAILED"] as const,
              }}
            >
              <Chip
                size="small"
                variant="filled"
                color="error"
                clickable
                label={t("{count} errors", {
                  ns: "Integrations",
                  count: failed,
                })}
              />
            </ModalOpenButton>
          )}
        </Stack>
        <Box sx={{ flexShrink: 0 }}>{viewLogButton}</Box>
      </Stack>
    </Paper>
  );
}
